/**
 * PeerLink — a real browser-to-browser WebRTC data channel implementing
 * CapsuleTransport, so the SessionContainer ships serialized-capsule blobs over
 * it like any other transport.
 *
 * Two ways to exchange the (non-trickle) SDP signaling:
 *   1. Copy/paste (`createInvite` / `acceptInvite`) — the invite code carries the
 *      SDP *and* the session seed, so pasting it hands the peer the seed over one
 *      OOB channel (honest-scope note in handshake.ts). Fallback / expert path.
 *   2. Hosted rendezvous (`createOffer` / `acceptOffer`) — the codes carry ONLY
 *      SDP + meta. The seed is carried SEPARATELY (the app puts it in the
 *      share-link fragment), so the relay never sees it. This is the path that
 *      retires copy/paste.
 *
 * ICE: multi-STUN + minted TURN (see iceConfig.ts). A 25s connect timeout marks
 * the link 'failed' rather than hanging; `acceptAnswer` is idempotent.
 */
import type { CapsuleTransport } from '../transport/transport.js';
import { seedFromJson, seedToJson, type SessionSeed } from '../session/handshake.js';
import { getIceConfig } from './iceConfig.js';

export type LinkState = 'new' | 'connecting' | 'connected' | 'closed' | 'failed';

function encodeCode(obj: unknown): string {
  return btoa(unescape(encodeURIComponent(JSON.stringify(obj))));
}
function decodeCode(code: string): any {
  return JSON.parse(decodeURIComponent(escape(atob(code.trim()))));
}

/** Resolve once ICE gathering completes, or after a fallback timeout. */
function waitIceComplete(pc: RTCPeerConnection, timeoutMs = 6000): Promise<void> {
  return new Promise((resolve) => {
    if (pc.iceGatheringState === 'complete') return resolve();
    const finish = (): void => {
      pc.removeEventListener('icegatheringstatechange', check);
      clearTimeout(timer);
      resolve();
    };
    const check = (): void => {
      if (pc.iceGatheringState === 'complete') finish();
    };
    pc.addEventListener('icegatheringstatechange', check);
    const timer = setTimeout(finish, timeoutMs);
  });
}

export function codeRole(code: string): 'offer' | 'answer' | null {
  try {
    const r = decodeCode(code).role;
    return r === 'offer' || r === 'answer' ? r : null;
  } catch {
    return null;
  }
}

export class PeerLink implements CapsuleTransport {
  readonly kind = 'webrtc' as const;
  onStateChange?: (state: LinkState) => void;
  onOpen?: () => void;

  private readonly pc: RTCPeerConnection;
  private dc: RTCDataChannel | null = null;
  private handler: ((bytes: Uint8Array) => void) | null = null;
  private _state: LinkState = 'new';
  private connectTimer: ReturnType<typeof setTimeout> | undefined;

  private constructor(pc: RTCPeerConnection) {
    this.pc = pc;
    pc.addEventListener('connectionstatechange', () => {
      if (pc.connectionState === 'failed') this.setState('failed');
      else if (pc.connectionState === 'closed') this.setState('closed');
    });
    pc.addEventListener('iceconnectionstatechange', () => {
      if (pc.iceConnectionState === 'failed') this.setState('failed');
    });
  }

  get ready(): boolean {
    return this._state === 'connected';
  }
  get status(): string {
    return `peer link: ${this._state}`;
  }
  get state(): LinkState {
    return this._state;
  }

  private setState(state: LinkState): void {
    if (this._state === state) return;
    this._state = state;
    if (state === 'connected' || state === 'closed' || state === 'failed') {
      if (this.connectTimer) {
        clearTimeout(this.connectTimer);
        this.connectTimer = undefined;
      }
    }
    this.onStateChange?.(state);
  }

  /** If the data channel hasn't opened in time, surface a failure (likely NAT / needs TURN). */
  private armConnectTimeout(): void {
    if (this.connectTimer) clearTimeout(this.connectTimer);
    this.connectTimer = setTimeout(() => {
      if (this._state !== 'connected') this.setState('failed');
    }, 25000);
  }

  private wireChannel(dc: RTCDataChannel): void {
    this.dc = dc;
    dc.binaryType = 'arraybuffer';
    dc.addEventListener('open', () => {
      this.setState('connected');
      this.onOpen?.();
    });
    dc.addEventListener('close', () => this.setState('closed'));
    dc.addEventListener('message', (e: MessageEvent) => {
      this.handler?.(new Uint8Array(e.data as ArrayBuffer));
    });
  }

  /** Initiator internals: a link with a data channel and a gathered local offer. */
  private static async newOfferLink(): Promise<{ link: PeerLink; sdp: RTCSessionDescription | null }> {
    const { iceServers } = await getIceConfig();
    const pc = new RTCPeerConnection({ iceServers });
    const link = new PeerLink(pc);
    link.setState('connecting');
    link.armConnectTimeout();
    link.wireChannel(pc.createDataChannel('fume-capsules', { ordered: true }));
    await pc.setLocalDescription(await pc.createOffer());
    await waitIceComplete(pc);
    return { link, sdp: pc.localDescription };
  }

  /** Responder internals: consume a remote offer, produce a gathered local answer. */
  private static async newAnswerLink(
    offerSdp: RTCSessionDescriptionInit,
  ): Promise<{ link: PeerLink; sdp: RTCSessionDescription | null }> {
    const { iceServers } = await getIceConfig();
    const pc = new RTCPeerConnection({ iceServers });
    const link = new PeerLink(pc);
    link.setState('connecting');
    link.armConnectTimeout();
    pc.ondatachannel = (e) => link.wireChannel(e.channel);
    await pc.setRemoteDescription(offerSdp);
    await pc.setLocalDescription(await pc.createAnswer());
    await waitIceComplete(pc);
    return { link, sdp: pc.localDescription };
  }

  // ---- Copy/paste path: seed rides inside the code ------------------------

  /** Initiator: create the invite code (offer + seed). */
  static async createInvite(
    seed: SessionSeed,
    meta: Record<string, unknown> = {},
  ): Promise<{ link: PeerLink; inviteCode: string }> {
    const { link, sdp } = await PeerLink.newOfferLink();
    const inviteCode = encodeCode({ v: 1, role: 'offer', sdp, seed: seedToJson(seed), meta });
    return { link, inviteCode };
  }

  /** Responder: consume an invite code, return the answer code + shared seed. */
  static async acceptInvite(
    inviteCode: string,
  ): Promise<{ link: PeerLink; answerCode: string; seed: SessionSeed; meta: Record<string, unknown> }> {
    const data = decodeCode(inviteCode);
    if (data.role !== 'offer') throw new Error('That is not an invite code.');
    const seed = seedFromJson(data.seed);
    const { link, sdp } = await PeerLink.newAnswerLink(data.sdp);
    const answerCode = encodeCode({ v: 1, role: 'answer', sdp });
    const meta = (data.meta ?? {}) as Record<string, unknown>;
    return { link, answerCode, seed, meta };
  }

  // ---- Hosted-rendezvous path: SDP only, NO seed in the code --------------

  /** Initiator: create an offer code carrying ONLY the SDP + meta (seed travels the fragment). */
  static async createOffer(
    meta: Record<string, unknown> = {},
  ): Promise<{ link: PeerLink; offerCode: string }> {
    const { link, sdp } = await PeerLink.newOfferLink();
    const offerCode = encodeCode({ v: 1, role: 'offer', sdp, meta });
    return { link, offerCode };
  }

  /** Responder: consume a seedless offer code, return the answer code + meta. */
  static async acceptOffer(
    offerCode: string,
  ): Promise<{ link: PeerLink; answerCode: string; meta: Record<string, unknown> }> {
    const data = decodeCode(offerCode);
    if (data.role !== 'offer') throw new Error('That is not an offer.');
    const { link, sdp } = await PeerLink.newAnswerLink(data.sdp);
    const answerCode = encodeCode({ v: 1, role: 'answer', sdp });
    const meta = (data.meta ?? {}) as Record<string, unknown>;
    return { link, answerCode, meta };
  }

  /** Initiator: finish the handshake with the responder's answer code. */
  async acceptAnswer(answerCode: string): Promise<void> {
    const data = decodeCode(answerCode);
    if (data.role !== 'answer') throw new Error('That is not an answer code.');
    // Only valid once, from have-local-offer. If the answer was already applied
    // (e.g. Connect clicked twice) the state is 'stable'/connected — ignore
    // instead of throwing "Called in wrong state: stable".
    if (this.pc.signalingState !== 'have-local-offer') return;
    await this.pc.setRemoteDescription(data.sdp);
  }

  send(bytes: Uint8Array): void {
    if (!this.dc || this.dc.readyState !== 'open') throw new Error('peer link not open');
    // Ship a fresh ArrayBuffer copy (decouples from the caller's view and picks
    // the unambiguous RTCDataChannel.send(ArrayBuffer) overload).
    this.dc.send(new Uint8Array(bytes).buffer as ArrayBuffer);
  }
  onMessage(handler: (bytes: Uint8Array) => void): void {
    this.handler = handler;
  }
  close(): void {
    try {
      this.dc?.close();
    } catch {
      /* noop */
    }
    try {
      this.pc.close();
    } catch {
      /* noop */
    }
    this.setState('closed');
  }
}
